import React, { useState, useEffect, useRef } from 'react'
import Icon from '@forwardflow/ui/Icons'

interface AudioPlayerProps {
   audioRef: React.RefObject<HTMLAudioElement>
   onError?: () => void
   audioSrc: string
   isLoading?: boolean
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
   audioRef,
   onError,
   audioSrc,
   isLoading = false
}) => {
   const [isPlaying, setIsPlaying] = useState(false)
   const [currentTime, setCurrentTime] = useState(0)
   const [duration, setDuration] = useState(0)
   const progressBarRef = useRef<HTMLDivElement>(null)

   useEffect(() => {
      setIsPlaying(false)
   }, [audioSrc])

   useEffect(() => {
      const audio = audioRef.current
      if (!audio) return

      const handleTimeUpdate = () => setCurrentTime(audio.currentTime)
      const handleDurationChange = () => setDuration(audio.duration)
      const handleEnded = () => {
         setIsPlaying(false)
      }
      const handleError = () => {
         setIsPlaying(false)
         if (onError) onError()
      }

      audio.addEventListener('timeupdate', handleTimeUpdate)
      audio.addEventListener('durationchange', handleDurationChange)
      audio.addEventListener('ended', handleEnded)
      audio.addEventListener('error', handleError)

      return () => {
         audio.removeEventListener('timeupdate', handleTimeUpdate)
         audio.removeEventListener('durationchange', handleDurationChange)
         audio.removeEventListener('ended', handleEnded)
         audio.removeEventListener('error', handleError)
      }
   }, [audioRef, onError])

   const togglePlayPause = () => {
      const audio = audioRef.current
      if (!audio) return

      if (isLoading) return

      if (isPlaying) {
         audio.pause()
         setIsPlaying(false)
      } else {
         audio.play().then(() => {
            setIsPlaying(true)
         }).catch(error => {
            console.error("Audio play error:", error)
            setIsPlaying(false)
            if (onError) onError()
         })
      }
   }

   const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const audio = audioRef.current
      const progressBar = progressBarRef.current
      if (!audio || !progressBar) return

      const rect = progressBar.getBoundingClientRect()
      const clickPosition = (e.clientX - rect.left) / rect.width
      const newTime = clickPosition * duration

      audio.currentTime = newTime
      setCurrentTime(newTime)
   }

   const formatTime = (timeInSeconds: number) => {
      const minutes = Math.floor(timeInSeconds / 60)
      const seconds = Math.floor(timeInSeconds % 60)
      return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
   }

   return (
      <div
         className={`flex items-center w-full gap-3 pr-5 rounded-full  ${isPlaying ? 'bg-black/20' : ''
            }`}
      >
         <audio ref={audioRef} src={audioSrc} preload="auto" />

         <button
            onClick={togglePlayPause}
            disabled={isLoading}
            className={`flex items-center justify-center w-[50px] h-10 rounded-full ${isLoading ? 'bg-black/10 cursor-wait' : 'bg-black/20 hover:bg-black/30'
               } focus:outline-none`}
         >
            {isLoading ? (
               <Icon name="Loader"
                  className="scale-90 fill-white"
                  width={16}
                  height={16} />
            ) : isPlaying ? (
               <Icon
                  name="Pause"
                  className="scale-125 fill-white"
                  width={16}
                  height={16}
               />
            ) : (
               <Icon
                  name="Play"
                  className="scale-90 fill-white"
                  width={16}
                  height={16}
               />
            )}
         </button>
         {isPlaying && (
            <>
               <div className="flex-1 mx-2">
                  <div
                     ref={progressBarRef}
                     className="relative h-2 rounded cursor-pointer bg-white/30"
                     onClick={handleProgressClick}
                  >
                     <div
                        className="absolute top-0 left-0 h-full bg-white rounded"
                        style={{ width: `${(currentTime / duration) * 100}%` }}
                     />
                  </div>
               </div>

               <div className="text-xs text-white">
                  {formatTime(currentTime)} / {formatTime(duration)}
               </div>
            </>
         )}
      </div>
   )
}